import { FC, ReactElement, useEffect } from 'react';
import { DialogContent, Stack } from '@mui/material';
import { z } from 'zod';
import { useIntl } from 'react-intl';
import { ControlledFormField } from '../../../form/ControlledFormField';
import { FormErrors } from '../../../form/FormErrors';
import { JourneySelect } from '../../../journey/JourneySelect';
import { PhaseSelect } from '../../../journey/PhaseSelect';
import { ActivitySelect } from '../../../journey/ActivitySelect';
import { useTypedForm } from '../../../../../shared/fe/hooks/useForm';
import { graphql } from '../../../gql';
import { useApiQuery } from '../../../query/hooks';

const getActivityQuery = graphql(`
	query createMetricStepLocation_getActivity($id: UUID!) {
		getActivity(by: { id: $id }) {
			id
			touchpoint {
				phase {
					id
					journey {
						id
					}
				}
			}
		}
	}
`);

const createFormSchema = () =>
	z.object({
		journeyId: z.string().optional(),
		phaseId: z.string().optional(),
		activityId: z.string().optional(),
	});

type FormValues = z.infer<ReturnType<typeof createFormSchema>>;

type SubmitValues = Readonly<{
	activityId?: string;
}>;

type Props = Readonly<{
	stepper: ReactElement;
	actions: ReactElement;
	onSubmit: (values: SubmitValues) => void;
	activityId?: string;
	onCompleteChange: (isComplete: boolean) => void;
}>;

const CreateMetricStepLocation: FC<Props> = ({ stepper, actions, onSubmit, activityId, onCompleteChange }) => {
	const { data, isLoading } = useApiQuery(getActivityQuery, {
		enabled: activityId != null,
		variables: {
			id: activityId ?? '',
		},
	});

	const intl = useIntl();

	const form = useTypedForm({
		schema: createFormSchema(),
	});

	useEffect(() => {
		const journeyId = data?.getActivity?.touchpoint?.phase?.journey?.id;
		const phaseId = data?.getActivity?.touchpoint?.phase?.id;
		const activityId = data?.getActivity?.id;

		if (journeyId != null && phaseId != null && activityId != null) {
			form.reset({
				journeyId,
				phaseId,
				activityId,
			});
		}
	}, [form, data]);

	const handleSubmit = (values: FormValues) => {
		onSubmit({
			activityId: values.activityId,
		});
	};

	const values = form.watch();
	const isComplete = values.journeyId != null && values.phaseId != null && values.activityId != null;

	useEffect(() => {
		onCompleteChange(isComplete);
	}, [onCompleteChange, isComplete]);

	return (
		<form noValidate onSubmit={form.handleSubmit(handleSubmit)}>
			<DialogContent>
				{stepper}

				<Stack spacing={3}>
					<ControlledFormField
						form={form}
						name={'journeyId'}
						label={intl.formatMessage({ id: 'form.create-metric.label.journey' })}
						description={intl.formatMessage({ id: 'form.create-metric.label.journey.description' })}
						render={(props) => <JourneySelect loading={isLoading} {...props} />}
					/>

					<ControlledFormField
						form={form}
						name={'phaseId'}
						label={intl.formatMessage({ id: 'form.create-metric.label.phase' })}
						render={(props) => <PhaseSelect loading={isLoading} journeyId={values.journeyId} {...props} />}
					/>

					<ControlledFormField
						form={form}
						name={'activityId'}
						label={intl.formatMessage({ id: 'form.create-metric.label.activity' })}
						render={(props) => <ActivitySelect loading={isLoading} phaseId={values.phaseId} {...props} />}
					/>

					<FormErrors form={form} />
				</Stack>
			</DialogContent>
			{actions}
		</form>
	);
};

export { CreateMetricStepLocation, type SubmitValues as SubmitValuesCreateMetricStepLocation };
